<!-- src/apps/secret/views/legal/PrivacyView.vue -->

<script setup lang="ts">
  /**
   * Política de Privacidade (LGPD — Lei 13.709/2018).
   *
   * Conteúdo jurídico longo, escrito inline em pt_BR por decisão deliberada:
   * páginas legais mudam como um documento único (revisão de advogado, data de
   * "última atualização") e não como micro-strings de UI, então fatiá-las em
   * dezenas de chaves i18n tornaria cada revisão um diff ilegível. O produto é
   * lançado para o mercado brasileiro; uma versão en resumida pode ser
   * adicionada depois como um segundo bloco/rota.
   *
   * As afirmações factuais espelham o que o backend realmente faz:
   * AES-256-GCM em repouso (familia encrypted_fields), destruição atômica na
   * primeira leitura, TTL nativo no Valkey/Redis, hash bcrypt de senhas e
   * passphrases. Análise de uso via Google Analytics 4 é opcional e só ocorre
   * mediante consentimento explícito (banner de cookies); sem consentimento
   * nada do Google é carregado.
   *
   * TEXTO x ANEXOS: o conteúdo em TEXTO é cifrado no servidor (AES-256-GCM em
   * repouso) — não é client-side/zero-knowledge. Já os ANEXOS de arquivo são
   * criptografia de PONTA A PONTA no navegador (chave só no fragmento do link,
   * nunca no servidor); armazenados cifrados no Cloudflare R2. O documento
   * distingue os dois regimes com precisão.
   */
  import { useCookieConsent } from '@/shared/composables/useCookieConsent';

  const { openBanner, analyticsConfigured } = useCookieConsent();

  const LAST_UPDATED = '11 de agosto de 2026';

  const sections = [
    { id: 'controlador', n: '01', title: 'Quem somos (Controlador)' },
    { id: 'dados', n: '02', title: 'Quais dados tratamos' },
    { id: 'bases', n: '03', title: 'Bases legais (art. 7º da LGPD)' },
    { id: 'direitos', n: '04', title: 'Seus direitos (art. 18 da LGPD)' },
    { id: 'compartilhamento', n: '05', title: 'Compartilhamento de dados' },
    { id: 'seguranca', n: '06', title: 'Segurança' },
    { id: 'retencao', n: '07', title: 'Retenção e eliminação' },
    { id: 'transferencia', n: '08', title: 'Transferência internacional' },
    { id: 'cookies', n: '09', title: 'Cookies' },
    { id: 'dpo', n: '10', title: 'Encarregado (DPO)' },
    { id: 'alteracoes', n: '11', title: 'Alterações e foro' },
  ];
</script>

<template>
  <article class="mx-auto w-full max-w-3xl px-5 pb-16 sm:px-8">
    <!-- Back link -->
    <router-link
      to="/"
      class="inline-flex items-center gap-2 pt-2 font-mono text-[11px] tracking-[0.08em] text-day-ink-faint
        uppercase transition-colors hover:text-brand-600
        dark:text-night-ink-faint dark:hover:text-brand-500">
      <span aria-hidden="true">&larr;</span>
      voltar ao início
    </router-link>

    <!-- Header -->
    <header class="mt-8 border-b border-day-line pb-8 dark:border-white/[0.07]">
      <p class="mb-3 font-mono text-[10px] tracking-[0.1em] text-brand-600 uppercase dark:text-brand-500">
        documento legal
      </p>
      <h1 class="font-brand text-[34px] leading-[1.15] font-bold text-day-ink sm:text-[40px] dark:text-night-ink">
        Política de Privacidade
      </h1>
      <p class="mt-4 font-mono text-[11px] tracking-[0.04em] text-day-ink-faint dark:text-night-ink-faint">
        Última atualização: {{ LAST_UPDATED }}
      </p>
      <p
        class="mt-5 border border-day-line bg-day-100 px-4 py-3 text-[13px] leading-[1.65] font-light
          text-day-ink-muted dark:border-white/[0.07] dark:bg-night-100 dark:text-night-ink-muted">
        Este documento descreve como o SeuID trata dados pessoais, em conformidade com a Lei
        Geral de Proteção de Dados (Lei nº 13.709/2018 — LGPD). O texto pode ser revisado
        periodicamente; a data acima indica a versão vigente.
      </p>
    </header>

    <!-- Index -->
    <nav
      aria-label="Sumário"
      class="border-b border-day-line py-6 dark:border-white/[0.07]">
      <ol class="grid grid-cols-1 gap-x-8 gap-y-1.5 sm:grid-cols-2">
        <li
          v-for="s in sections"
          :key="s.id">
          <a
            :href="`#${s.id}`"
            class="group inline-flex items-baseline gap-2 text-[13px] text-day-ink-muted
              transition-colors hover:text-brand-600 dark:text-night-ink-muted dark:hover:text-brand-500">
            <span class="font-mono text-[10px] text-brand-600 dark:text-brand-500">{{ s.n }}.</span>
            {{ s.title }}
          </a>
        </li>
      </ol>
    </nav>

    <div
      class="space-y-10 pt-10 text-[15px] leading-[1.75] font-light text-day-ink-muted dark:text-night-ink-muted
        [&_h2]:mb-3 [&_h2]:flex [&_h2]:items-baseline [&_h2]:gap-3 [&_h2]:font-brand [&_h2]:text-[22px]
        [&_h2]:leading-snug [&_h2]:font-semibold [&_h2]:text-day-ink dark:[&_h2]:text-night-ink
        [&_h3]:mt-5 [&_h3]:mb-1.5 [&_h3]:text-[15px] [&_h3]:font-semibold [&_h3]:text-day-ink dark:[&_h3]:text-night-ink
        [&_ul]:list-disc [&_ul]:space-y-1.5 [&_ul]:pl-5">
      <section
        id="controlador"
        class="scroll-mt-24">
        <h2>
          <span class="font-mono text-[11px] font-normal text-brand-600 dark:text-brand-500">01.</span>
          Quem somos (Controlador)
        </h2>
        <p>
          O controlador dos dados pessoais tratados neste serviço é o <strong>SeuID</strong>
          (seuid.com.br), serviço de compartilhamento de mensagens de uso único. Canal de
          contato para assuntos de privacidade:
          <a
            href="mailto:suporte@seuid.com.br"
            class="text-brand-600 underline underline-offset-2 hover:no-underline dark:text-brand-500">suporte@seuid.com.br</a>.
        </p>
      </section>

      <section
        id="dados"
        class="scroll-mt-24">
        <h2>
          <span class="font-mono text-[11px] font-normal text-brand-600 dark:text-brand-500">02.</span>
          Quais dados tratamos
        </h2>
        <h3>a) Segredos (o conteúdo que você compartilha)</h3>
        <ul>
          <li>
            São cifrados em repouso no servidor com <strong>AES-256-GCM</strong> antes de serem
            gravados.
          </li>
          <li>
            São armazenados de forma <strong>efêmera</strong> em banco de dados em memória
            (Valkey/Redis) e <strong>destruídos na primeira leitura</strong> ou ao expirar o
            prazo (TTL) que você definiu — o que ocorrer primeiro.
          </li>
          <li>
            O SeuID <strong>não faz backup de segredos</strong>. Depois de lido ou expirado, o
            conteúdo é irrecuperável, inclusive para nós.
          </li>
          <li>
            Não acessamos nem inspecionamos o conteúdo dos segredos no curso normal da
            operação do serviço.
          </li>
        </ul>
        <h3>b) Anexos de arquivo (criptografia ponta a ponta)</h3>
        <ul>
          <li>
            Você pode anexar arquivos a um segredo. Diferentemente do texto, os anexos são
            <strong>cifrados de ponta a ponta no seu próprio navegador</strong>
            (AES-256-GCM): a chave de decifração viaja apenas no fragmento do link
            (o trecho após o <code>#</code>) e <strong>nunca é enviada ao SeuID</strong>. O
            servidor armazena e movimenta apenas o arquivo já cifrado, que
            <strong>não conseguimos ler</strong>.
          </li>
          <li>
            O arquivo cifrado é guardado em <strong>armazenamento de objetos da
              Cloudflare (R2)</strong>, na região disponível mais próxima do Brasil (Leste
            da América do Norte) — ver as seções
            <a
              href="#compartilhamento"
              class="text-brand-600 underline underline-offset-2 hover:no-underline dark:text-brand-500">Compartilhamento de dados</a>
            e
            <a
              href="#transferencia"
              class="text-brand-600 underline underline-offset-2 hover:no-underline dark:text-brand-500">Transferência internacional</a>.
          </li>
          <li>
            O anexo é <strong>apagado do armazenamento</strong> assim que é baixado pelo
            destinatário ou quando o segredo é queimado; uma regra de expiração remove
            qualquer arquivo remanescente em até <strong>30 dias</strong> (ver
            <a
              href="#retencao"
              class="text-brand-600 underline underline-offset-2 hover:no-underline dark:text-brand-500">Retenção e eliminação</a>).
          </li>
          <li>
            Como a chave existe apenas no link, se você perder o link o arquivo é
            <strong>irrecuperável</strong>, inclusive para nós.
          </li>
        </ul>
        <h3>c) Dados de conta</h3>
        <ul>
          <li>Endereço de e-mail (usado para login, verificação e comunicações operacionais).</li>
          <li>
            <strong>Hash</strong> da senha (bcrypt) — nunca armazenamos a senha em texto claro.
            O mesmo vale para passphrases opcionais de segredos: guardamos apenas o hash.
          </li>
        </ul>
        <h3>d) Metadados operacionais mínimos</h3>
        <ul>
          <li>
            Registros de acesso (como endereço IP, data e hora), mantidos para segurança,
            prevenção a abuso e cumprimento do Marco Civil da Internet, com retenção limitada.
          </li>
          <li>
            <strong>Destino de entrega:</strong> se você optar por enviar o segredo por
            <strong>e-mail</strong> ou <strong>WhatsApp</strong>, o endereço de e-mail ou o
            número de telefone que você informar é armazenado no <strong>recibo</strong> do
            segredo, para que você possa acompanhar na sua área para onde o link foi enviado.
            Esse dado segue a mesma <a
              href="#retencao"
              class="text-brand-600 underline underline-offset-2 hover:no-underline dark:text-brand-500">retenção</a> do recibo e é eliminado junto com ele. O envio por WhatsApp é aberto no seu
            próprio aplicativo — o SeuID não envia mensagens de WhatsApp em seu nome.
          </li>
        </ul>
        <h3>e) Dados de análise de uso (Google Analytics) — só com consentimento</h3>
        <ul>
          <li>
            Se — e somente se — você autorizar no banner de cookies, usamos o
            <strong>Google Analytics 4</strong> para entender de forma agregada como o site é
            utilizado (páginas visitadas, tipo de dispositivo/navegador, origem da visita e
            interações). O <strong>IP é anonimizado</strong> (<code>anonymize_ip</code>).
          </li>
          <li>
            Esses dados servem para melhorar o produto, <strong>nunca</strong> para publicidade
            direcionada, e <strong>não</strong> são cruzados com o conteúdo dos seus segredos.
          </li>
          <li>
            <strong>Sem o seu consentimento, nenhum código do Google é carregado</strong> — nem
            scripts, nem cookies, nem requisições. Você pode revogar a autorização a qualquer
            momento (veja a seção <a
              href="#cookies"
              class="text-brand-600 underline underline-offset-2 hover:no-underline dark:text-brand-500">Cookies</a>).
          </li>
        </ul>
      </section>

      <section
        id="bases"
        class="scroll-mt-24">
        <h2>
          <span class="font-mono text-[11px] font-normal text-brand-600 dark:text-brand-500">03.</span>
          Bases legais (art. 7º da LGPD)
        </h2>
        <ul>
          <li>
            <strong>Execução de contrato</strong> (art. 7º, V): tratamento necessário para
            prestar o serviço que você contratou — criar, entregar e destruir segredos, manter
            sua conta.
          </li>
          <li>
            <strong>Legítimo interesse</strong> (art. 7º, IX): segurança da plataforma,
            prevenção a fraude e abuso, registros técnicos de acesso.
          </li>
          <li>
            <strong>Cumprimento de obrigação legal</strong> (art. 7º, II): guarda de registros
            de acesso quando exigida por lei.
          </li>
          <li>
            <strong>Consentimento</strong> (art. 7º, I): quando aplicável, para tratamentos não
            cobertos pelas bases acima — sempre revogável.
          </li>
        </ul>
      </section>

      <section
        id="direitos"
        class="scroll-mt-24">
        <h2>
          <span class="font-mono text-[11px] font-normal text-brand-600 dark:text-brand-500">04.</span>
          Seus direitos (art. 18 da LGPD)
        </h2>
        <p>Como titular de dados pessoais, você pode solicitar a qualquer momento:</p>
        <ul class="mt-2">
          <li>confirmação da existência de tratamento e acesso aos seus dados;</li>
          <li>correção de dados incompletos, inexatos ou desatualizados;</li>
          <li>anonimização, bloqueio ou eliminação de dados desnecessários ou excessivos;</li>
          <li>portabilidade dos dados a outro fornecedor de serviço;</li>
          <li>informação sobre com quem compartilhamos seus dados;</li>
          <li>revogação do consentimento, quando essa for a base legal;</li>
          <li>eliminação dos dados tratados com base no consentimento.</li>
        </ul>
        <p class="mt-3">
          Para exercer qualquer direito, escreva para
          <a
            href="mailto:suporte@seuid.com.br"
            class="text-brand-600 underline underline-offset-2 hover:no-underline dark:text-brand-500">suporte@seuid.com.br</a>. Responderemos nos prazos da LGPD. Observação: segredos já lidos ou expirados não
          podem ser acessados nem recuperados por ninguém — a destruição é definitiva por
          desenho do sistema.
        </p>
      </section>

      <section
        id="compartilhamento"
        class="scroll-mt-24">
        <h2>
          <span class="font-mono text-[11px] font-normal text-brand-600 dark:text-brand-500">05.</span>
          Compartilhamento de dados
        </h2>
        <ul>
          <li><strong>Não vendemos</strong> dados pessoais, em nenhuma hipótese.</li>
          <li>
            Usamos sub-operadores de infraestrutura (provedores de hospedagem e envio de
            e-mail transacional) estritamente para operar o serviço, sob obrigações contratuais
            de confidencialidade e proteção de dados.
          </li>
          <li>
            <strong>Cloudflare, Inc.</strong> atua como operador para o
            <strong>armazenamento dos anexos cifrados</strong> (Cloudflare R2). Como o anexo
            é cifrado de ponta a ponta no seu navegador, a Cloudflare — assim como o SeuID —
            armazena apenas conteúdo cifrado que <strong>não consegue ler</strong>. Esse
            armazenamento ocorre fora do Brasil (ver seção 08).
          </li>
          <li>
            <strong>Mediante o seu consentimento</strong>, dados de análise de uso são tratados
            pelo <strong>Google</strong> (Google Analytics 4) como operador. Esse compartilhamento
            só existe se você aceitar o banner de cookies e cessa quando você o revoga. Pode
            envolver transferência internacional (ver seção 08).
          </li>
          <li>
            Podemos divulgar dados quando exigido por lei ou por ordem de autoridade
            competente, no limite do estritamente necessário.
          </li>
        </ul>
      </section>

      <section
        id="seguranca"
        class="scroll-mt-24">
        <h2>
          <span class="font-mono text-[11px] font-normal text-brand-600 dark:text-brand-500">06.</span>
          Segurança
        </h2>
        <ul>
          <li>Cifra em repouso dos segredos com AES-256-GCM.</li>
          <li>Criptografia em trânsito (TLS) em todas as conexões.</li>
          <li>Senhas e passphrases armazenadas apenas como hash (bcrypt).</li>
          <li>Controle de acesso restrito à infraestrutura de produção.</li>
        </ul>
        <p class="mt-3">
          Nenhum sistema é infalível. Em caso de incidente de segurança com risco relevante aos
          titulares, comunicaremos os afetados e a ANPD conforme o art. 48 da LGPD.
        </p>
      </section>

      <section
        id="retencao"
        class="scroll-mt-24">
        <h2>
          <span class="font-mono text-[11px] font-normal text-brand-600 dark:text-brand-500">07.</span>
          Retenção e eliminação
        </h2>
        <ul>
          <li>
            <strong>Segredos:</strong> efêmeros por desenho — destruídos na primeira leitura ou
            na expiração do prazo definido, sem backup.
          </li>
          <li>
            <strong>Anexos:</strong> o arquivo cifrado é apagado do armazenamento assim que é
            baixado pelo destinatário ou quando o segredo é queimado. Uma regra de expiração
            (<em>lifecycle</em>) do armazenamento remove qualquer arquivo remanescente em até
            <strong>30 dias</strong>.
          </li>
          <li>
            <strong>Conta:</strong> mantida enquanto ativa; você pode solicitar a exclusão a
            qualquer momento pelo canal de suporte.
          </li>
          <li>
            <strong>Registros de acesso:</strong> retenção limitada ao necessário para
            segurança e às obrigações legais aplicáveis.
          </li>
        </ul>
      </section>

      <section
        id="transferencia"
        class="scroll-mt-24">
        <h2>
          <span class="font-mono text-[11px] font-normal text-brand-600 dark:text-brand-500">08.</span>
          Transferência internacional
        </h2>
        <p>
          Parte da infraestrutura do SeuID pode estar localizada fora do Brasil. Atualmente:
        </p>
        <ul>
          <li>
            o <strong>banco de dados de contas</strong> é hospedado em região dos
            <strong>Estados Unidos</strong>;
          </li>
          <li>
            os <strong>anexos cifrados</strong> são armazenados na Cloudflare (R2). Como a
            Cloudflare não oferece região na América do Sul, utilizamos a região disponível
            <strong>mais próxima do Brasil (Leste da América do Norte)</strong>. Ressalvamos
            que o anexo trafega e é armazenado <strong>cifrado de ponta a ponta</strong> — a
            chave permanece apenas com você, no link.
          </li>
        </ul>
        <p class="mt-3">
          Nessas hipóteses, a transferência internacional ocorre com salvaguardas contratuais
          junto aos provedores de infraestrutura, observando o art. 33 da LGPD. Se a
          localização dos dados mudar, esta política será atualizada.
        </p>
      </section>

      <section
        id="cookies"
        class="scroll-mt-24">
        <h2>
          <span class="font-mono text-[11px] font-normal text-brand-600 dark:text-brand-500">09.</span>
          Cookies
        </h2>
        <h3>Cookies essenciais</h3>
        <p>
          Usamos cookies <strong>essenciais</strong> de sessão e segurança, necessários para
          autenticação e proteção contra fraude. Eles não dependem de consentimento porque são
          indispensáveis para o serviço funcionar.
        </p>
        <h3>Análise (Google Analytics) — sujeita a consentimento</h3>
        <p>
          Usamos o <strong>Google Analytics 4</strong> apenas se você autorizar. A base legal é o
          seu <strong>consentimento</strong> (art. 7º, I e art. 8º da LGPD). Enquanto você não
          decidir, ou se você recusar, <strong>nenhum</strong> script, cookie ou requisição do
          Google é carregado. Recusar é tão simples quanto aceitar: um clique no banner.
        </p>
        <p class="mt-3">
          Você pode <strong>rever ou revogar</strong> a sua escolha a qualquer momento. Ao revogar
          um consentimento já dado, recarregue a página para encerrar totalmente a coleta na
          sessão atual.
        </p>
        <p
          v-if="analyticsConfigured"
          class="mt-4">
          <button
            type="button"
            data-testid="reopen-cookie-consent"
            class="inline-flex items-center gap-2 rounded-sm border border-day-line px-4 py-2 font-mono
              text-[11px] tracking-[0.08em] text-day-ink uppercase transition-colors hover:bg-day-100
              dark:border-white/[0.12] dark:text-night-ink dark:hover:bg-night-100"
            @click="openBanner">
            Gerenciar cookies
          </button>
        </p>
      </section>

      <section
        id="dpo"
        class="scroll-mt-24">
        <h2>
          <span class="font-mono text-[11px] font-normal text-brand-600 dark:text-brand-500">10.</span>
          Encarregado (DPO)
        </h2>
        <p>
          O canal do Encarregado pelo Tratamento de Dados Pessoais (art. 41 da LGPD) é
          <a
            href="mailto:suporte@seuid.com.br"
            class="text-brand-600 underline underline-offset-2 hover:no-underline dark:text-brand-500">suporte@seuid.com.br</a>. Use este endereço para dúvidas, reclamações e para exercer seus direitos como
          titular.
        </p>
      </section>

      <section
        id="alteracoes"
        class="scroll-mt-24">
        <h2>
          <span class="font-mono text-[11px] font-normal text-brand-600 dark:text-brand-500">11.</span>
          Alterações e foro
        </h2>
        <p>
          Podemos atualizar esta política para refletir mudanças no serviço ou na legislação; a
          versão vigente é sempre a publicada nesta página, com a data de atualização no topo.
          Alterações relevantes serão comunicadas pelos canais do serviço. Esta política é
          regida pelas leis da República Federativa do Brasil; fica eleito o foro do domicílio
          do titular, quando consumidor, ou, nos demais casos, o foro da comarca de São Paulo/SP.
        </p>
      </section>
    </div>

    <!-- Cross-link -->
    <footer class="mt-12 border-t border-day-line pt-6 dark:border-white/[0.07]">
      <router-link
        to="/termos"
        class="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.08em] text-brand-600
          uppercase transition-colors hover:text-brand-700 dark:text-brand-500 dark:hover:text-brand-400">
        Termos de Uso
        <span aria-hidden="true">&rarr;</span>
      </router-link>
    </footer>
  </article>
</template>
